import { useEffect, useRef, useState } from 'react';

import { Box, IconButton } from '@zillow/constellation-10';
import { IconArrowDownFilled } from '@zillow/constellation-icons';

import type React from 'react';

export type JumpToBottomButtonProps = {
  /** The scroll container the sentinel lives in. Omit to observe the viewport. */
  scrollRootRef?: React.RefObject<HTMLElement | null>;
};

export const JumpToBottomButton = ({ scrollRootRef }: JumpToBottomButtonProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isBottomVisible, setIsBottomVisible] = useState(true);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(([entry]) => setIsBottomVisible(entry.isIntersecting), {
      root: scrollRootRef?.current ?? null,
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [scrollRootRef]);

  return (
    <>
      <Box
        css={{
          position: 'sticky',
          bottom: 'default',
          zIndex: 2,
          display: 'flex',
          justifyContent: 'center',
          height: 0,
          overflow: 'visible',
          alignItems: 'flex-end',
          pointerEvents: 'none',
        }}
      >
        {!isBottomVisible && (
          <IconButton
            type="button"
            title="Jump to bottom"
            aria-label="Jump to bottom"
            data-testid="jump-to-bottom-button"
            tone="neutral"
            emphasis="outlined"
            shape="circle"
            size="md"
            icon={<IconArrowDownFilled />}
            onClick={() => sentinelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })}
            css={{
              pointerEvents: 'auto',
              backgroundColor: 'bg.neutral',
              boxShadow: 'sm',
            }}
          />
        )}
      </Box>
      <div ref={sentinelRef} data-testid="jump-to-bottom-sentinel" />
    </>
  );
};
